import { Link } from "react-router-dom";
import { observer } from "mobx-react";
import { Button, Text } from "@mantine/core";
import { BsFillPlayFill, BsPauseFill } from "react-icons/bs";
import { type ReactNode } from "react";
import { EpisodeMenu } from "./EpisodeMenu";
import { EpisodeProgress } from "./EpisodeProgress";
import styles from "./styles/EpisodeRow.module.css";
import { usePlayEpisode } from "@/hooks/usePlayEpisode";
import { useStores } from "@/hooks/useStores";
import { createDurationMin, filterText, filterTitle, formatPublished, timeAgo, truncate } from "@/core/helpers";
import { CoverImage } from "@/components/podcast/CoverImage";
import { useLoad } from "@/hooks/useLoad";
import { isVideoEpisode } from "@/core/video";

interface EpisodeRowProps {
    episode: Episode;
    podcast: Podcast;
    /** Show the podcast cover and name (lists that mix podcasts) */
    showPodcast?: boolean;
    /** Show a short description under the title */
    showSnippet?: boolean;
    /** Show the publication date instead of the age, for time-sensitive selections */
    showDate?: boolean;
    /** Extra controls rendered next to the play button */
    extra?: ReactNode;
    /** Show the "more actions" menu next to the play button */
    withMenu?: boolean;
    /** A short line under the title block, e.g. time remaining */
    note?: ReactNode;
    /** The place in a numbered list, shown as a badge on the cover (needs `showPodcast`) */
    rank?: number;
    /** The play button leads the row, and only `extra` stays on the right */
    playFirst?: boolean;
}

/**
 * One episode in a list: cover, title, meta line and a round play button.
 * Shared by the category feed and the podcast episode list.
 */
export const EpisodeRow = observer(
    ({
        episode,
        podcast,
        showPodcast = false,
        showSnippet = false,
        showDate = false,
        extra,
        withMenu = false,
        note,
        rank,
        playFirst = false,
    }: EpisodeRowProps) => {
        const { listeningStore } = useStores();
        const { play, pause, isPlaying } = usePlayEpisode(episode, podcast);
        const played = listeningStore.isPlayed(episode.id);
        const signedIn = listeningStore.signedIn;
        // Lists fetch the progress of the episodes they show; signing in later refetches it
        useLoad(`${episode.id}:${signedIn}`, () => listeningStore.watch([episode.id]), "EpisodeRow");
        const isVideo = isVideoEpisode(episode);
        const duration = episode.length ? createDurationMin(episode.length) : 0;

        const playControl = isVideo ? (
            <Button component={Link} size="compact-md" to={`/podd/${podcast.slug}/${episode.slug}`} variant="light">
                Titta
            </Button>
        ) : (
            <button
                aria-label={`${isPlaying ? "Pausa" : "Spela"} ${episode.title}`}
                className={styles.play}
                onClick={isPlaying ? pause : play}
                type="button">
                {isPlaying ? <BsPauseFill size={22} /> : <BsFillPlayFill size={22} />}
            </button>
        );

        return (
            <li className={`${styles.row} ${played ? styles.played : ""}`}>
                {showPodcast && (
                    <div className={styles.cover}>
                        <CoverImage podcast={podcast} />
                        {rank !== undefined && (
                            // The list is an <ol>, which already says the order
                            <span aria-hidden="true" className={styles.rank}>
                                {rank}
                            </span>
                        )}
                    </div>
                )}
                {playFirst && playControl}
                <div className={styles.text}>
                    <Link className={styles.title} to={`/podd/${podcast.slug}/${episode.slug}`}>
                        <span>{filterTitle(episode.title, podcast.name)}</span>
                    </Link>
                    {showSnippet && episode.snippet && (
                        <p className={styles.snippet}>{truncate(filterText(episode.snippet), 174)}</p>
                    )}
                    <div className={styles.meta}>
                        {showPodcast && (
                            <Link className={styles.show} to={`/podd/${podcast.slug}`}>
                                {podcast.name}
                            </Link>
                        )}
                        <Text c="dimmed" component="span" size="sm">
                            <time dateTime={episode.published}>
                                {showDate ? formatPublished(episode.published) : timeAgo(episode.published)}
                            </time>
                            {duration > 0 && ` · ${duration} min`}
                            {played && " · Spelad"}
                        </Text>
                    </div>
                    <EpisodeProgress episodeId={episode.id} showPlayed={false} />
                    {note}
                </div>
                <div className={styles.actions}>
                    {withMenu && <EpisodeMenu episode={episode} podcast={podcast} />}
                    {extra}
                    {!playFirst && playControl}
                </div>
            </li>
        );
    }
);
